"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, TrendingDown, Award, Users, Sparkles, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
const assurances = [{
  id: 1,
  title: "Energy Procurement",
  description: "Strategic commercial electricity and gas procurement to secure optimal contract structures and tariff rates.",
  badge: "Procurement"
}, {
  id: 2,
  title: "Audits & Grants",
  description: "Full management of SEAI energy audit vouchers and support to access government-backed efficiency funding.",
  badge: "SEAI Support"
}, {
  id: 3,
  title: "Project Coordination",
  description: "Coordinating vetted technical specialists, auditors, and contractors from initial assessment to project delivery.",
  badge: "Coordination"
}];
export default function Spendless() {
  const shouldReduceMotion = useReducedMotion();
  return <section data-section-id="65965" id="spend-less-save-more" data-nav="dark" className="dark bg-secondary text-secondary-foreground relative py-14 lg:py-18 overflow-hidden border-t border-border">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 opacity-20 overflow-hidden">
        <svg className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[750px] w-[1200px] text-border stroke-current" xmlns="http://www.w3.org/2000/svg" fill="none">
          <defs>
            <pattern id="collegiate-grid" width="48" height="48" patternUnits="userSpaceOnUse">
              <path d="M 48 0 L 0 0 0 48" fill="none" stroke="currentColor" strokeWidth="0.75" strokeDasharray="4 4" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#collegiate-grid)" />
          
          <path d="M 100 0 L 600 500 L 1100 0" stroke="currentColor" strokeWidth="1.2" opacity="0.4" />
          <path d="M 250 0 L 600 350 L 950 0" stroke="currentColor" strokeWidth="1" opacity="0.3" />
        </svg>

        <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-96 w-[42rem] rounded-lg bg-primary/20 blur-3xl" />
      </div>

      <div className="container mx-auto px-5 md:px-8 lg:px-12 max-w-6xl relative z-10">
        <div className="text-center max-w-3xl mx-auto">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.45,
          ease: "easeOut"
        }} className="flex justify-center mb-5">
            <Badge variant="outline" className="px-3.5 py-1.5 rounded-lg border-primary/30 bg-primary/10 text-primary backdrop-blur-xs text-xs font-semibold tracking-wide uppercase gap-2 shadow-xs">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75" />
                <span className="relative inline-flex rounded-lg h-2 w-2 bg-primary" />
              </span>
              <span>Commercial Energy Management & Procurement</span>
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="text-3xl sm:text-4xl lg:text-5xl font-sans font-bold tracking-tight text-secondary-foreground leading-[1.18]">
            Take control of your{" "}
            <span className="font-serif font-normal italic text-primary">
              commercial energy costs.
            </span>
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.5,
          delay: 0.18,
          ease: "easeOut"
        }} className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Cost Management Ireland provides specialized commercial energy management, market-wide procurement, and project coordination. We coordinate vetted technical specialists, auditors, and contractors to streamline your efficiency initiatives. There is no fee charged by CMI to the customer for our service.
          </motion.p>

          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.5,
          delay: 0.25,
          ease: "easeOut"
        }} className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4">
            <Button asChild size="lg" className="h-12 px-7 rounded-lg font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-md group">
              <Link to="/FreeEnergyReviewPage" className="inline-flex items-center justify-center gap-2">
                <span>Free Energy Review</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="h-12 px-7 rounded-lg font-semibold border-border bg-card/40 text-secondary-foreground hover:bg-card hover:text-secondary-foreground transition-colors shadow-xs">
              <Link to="/SeaiEnergyAuditAndEligibilityPage" className="inline-flex items-center justify-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <span>Check SEAI Eligibility</span>
              </Link>
            </Button>
          </motion.div>

          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.5,
          delay: 0.32,
          ease: "easeOut"
        }} className="mt-5 flex items-center justify-center gap-2 text-xs sm:text-sm text-muted-foreground">
            <PhoneCall className="h-3.5 w-3.5 text-primary" />
            <span>
              Need help? Call us at{" "}
              <a href="tel:040255333" className="font-medium text-secondary-foreground hover:text-primary underline underline-offset-2 transition-colors">
                0402 55333
              </a>{" "}
              for a callback.
            </span>
          </motion.div>
        </div>

        <div className="my-14 lg:my-16">
          <Separator className="bg-border/70" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {assurances.map((item, i) => <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.45,
          delay: shouldReduceMotion ? 0 : 0.1 * i,
          ease: "easeOut"
        }} className="relative p-6 rounded-xl bg-card/30 border border-border backdrop-blur-xs flex flex-col justify-between transition-[border-color,background-color] hover:bg-card/50 hover:border-border/80">
              <div>
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-primary/15 border border-primary/20 flex items-center justify-center text-primary">
                    {i === 0 && <TrendingDown className="h-5 w-5" />}
                    {i === 1 && <Award className="h-5 w-5" />}
                    {i === 2 && <Users className="h-5 w-5" />}
                  </div>
                  <Badge variant="outline" className="text-[11px] font-medium border-primary/25 text-primary bg-primary/10">
                    {item.badge}
                  </Badge>
                </div>

                <h3 className="text-base font-sans font-semibold text-secondary-foreground tracking-tight mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}